/**
 * Assign Study Modal
 *
 * Modal for assigning a study to a radiologist.
 * Handles both cases: when worklist item exists and when it needs to be created.
 */

import { useState, useEffect } from 'react'
import { Loader2, User, Clock, AlertCircle } from 'lucide-react'
import { useRadiologists, sortByWorkload, getTotalWorkload } from '@/application/users'
import { getErrorMessage } from '@/lib/api'
import { useWorklistItemByStudy, useCreateWorklistItem, useAssignWorklistItem, worklistKeys } from '@/application/worklist'
import { useDefaultPriority } from '@/application/priorities/usePriorities'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from '@/lib/toast'
import { Button } from '@/presentation/components/ui/Button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/presentation/components/ui/Dialog'
import { PrioritySelect } from '@/presentation/components/shared/PrioritySelect'
import { WORKLIST_STATUS_LABELS } from '@/config/constants'

// =============================================================================
// Types
// =============================================================================

interface AssignStudyModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  studyId: string | null
  studyDescription?: string
  onSuccess?: () => void
}

// =============================================================================
// Component
// =============================================================================

export function AssignStudyModal({
  open,
  onOpenChange,
  studyId,
  studyDescription,
  onSuccess,
}: AssignStudyModalProps) {
  const queryClient = useQueryClient()
  const [selectedRadiologistId, setSelectedRadiologistId] = useState<string>('')
  const [priorityId, setPriorityId] = useState<string>('')
  const [notes, setNotes] = useState<string>('')

  // Fetch existing worklist item for this study
  const { data: existingWorklistItem, isLoading: isLoadingWorklist } = useWorklistItemByStudy(
    open ? studyId ?? undefined : undefined
  )

  // Only fetch radiologists when modal is open
  const { data: radiologists, isLoading: isLoadingRadiologists } = useRadiologists({ enabled: open })

  // Get default priority
  const { data: defaultPriority } = useDefaultPriority()

  // Mutations
  const createWorklistMutation = useCreateWorklistItem()
  const assignMutation = useAssignWorklistItem()

  const sortedRadiologists = radiologists ? sortByWorkload(radiologists) : []
  const isLoading = isLoadingWorklist || isLoadingRadiologists
  const isMutating = createWorklistMutation.isPending || assignMutation.isPending

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setSelectedRadiologistId('')
      setPriorityId(defaultPriority?.id || '')
      setNotes('')
    }
  }, [open, defaultPriority])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!studyId || !selectedRadiologistId) return

    try {
      if (existingWorklistItem) {
        // Worklist item exists - just assign
        await assignMutation.mutateAsync({
          id: existingWorklistItem.id,
          data: {
            assignedTo: selectedRadiologistId,
            priorityId: priorityId || undefined,
            notes: notes || undefined,
          },
        })
      } else {
        // No worklist item - create and assign in one call
        await createWorklistMutation.mutateAsync({
          studyId,
          assignedTo: selectedRadiologistId,
          priorityId: priorityId || undefined,
          notes: notes || undefined,
        })
      }

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: worklistKeys.all })
      queryClient.invalidateQueries({ queryKey: ['studies'] })

      toast.success('Estudio asignado correctamente')
      handleClose()
      onSuccess?.()
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al asignar estudio'))
    }
  }

  const handleClose = () => {
    setSelectedRadiologistId('')
    setPriorityId('')
    setNotes('')
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogClose onClose={handleClose} />
        <DialogHeader>
          <DialogTitle>Asignar Estudio</DialogTitle>
          <DialogDescription>
            {studyDescription
              ? `Asignar "${studyDescription}" a un radiólogo`
              : 'Seleccione un radiólogo para asignar este estudio'}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {/* Info about existing worklist item */}
            {existingWorklistItem && existingWorklistItem.status === 'RETURNED' && (
              <div className="flex items-start gap-2 p-3 rounded-md alert-warning">
                <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium">Estudio devuelto</p>
                  {existingWorklistItem.returnReason && (
                    <p className="mt-1">
                      Motivo: {existingWorklistItem.returnReason}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Radiologist Selection */}
            <div>
              <label className="block text-sm font-medium text-[var(--foreground)] mb-2">
                Radiólogo <span className="text-red-500">*</span>
              </label>
              {sortedRadiologists.length === 0 ? (
                <p className="text-sm text-[var(--foreground-muted)]">
                  No hay radiólogos disponibles
                </p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto border rounded-md p-2">
                  {sortedRadiologists.map((radiologist) => {
                    const workload = getTotalWorkload(radiologist)
                    const isSelected = selectedRadiologistId === radiologist.id

                    return (
                      <button
                        key={radiologist.id}
                        type="button"
                        onClick={() => setSelectedRadiologistId(radiologist.id)}
                        className={
                          'w-full flex items-center justify-between p-3 rounded-md border transition-colors ' +
                          (isSelected
                            ? 'border-[var(--primary)] bg-[var(--primary)]/5'
                            : 'border-[var(--border)] hover:border-[var(--primary)]/50 hover:bg-[var(--background-subtle)]')
                        }
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-[var(--background-subtle)] rounded-full">
                            <User className="h-4 w-4 text-[var(--foreground-muted)]" />
                          </div>
                          <div className="text-left">
                            <p className="font-medium text-sm">{radiologist.fullName}</p>
                            <p className="text-xs text-[var(--foreground-muted)]">
                              {radiologist.pendingCount} {WORKLIST_STATUS_LABELS.PENDING.toLowerCase()} - {radiologist.inProgressCount} {WORKLIST_STATUS_LABELS.IN_PROGRESS.toLowerCase()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3 text-[var(--foreground-muted)]" />
                          <span className={
                            'text-sm font-medium ' +
                            (workload === 0
                              ? 'text-green-600 dark:text-green-400'
                              : workload < 5
                                ? 'text-yellow-600 dark:text-yellow-400'
                                : 'text-red-600 dark:text-red-400')
                          }>
                            {workload}
                          </span>
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-[var(--foreground)] mb-2">
                Prioridad
              </label>
              <PrioritySelect
                value={priorityId}
                onChange={setPriorityId}
                autoSelectDefault={true}
                showPreview={true}
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-[var(--foreground)] mb-2">
                Notas (opcional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Instrucciones especiales..."
                className="w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm resize-none"
                rows={3}
              />
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={!selectedRadiologistId || isMutating}
              >
                {isMutating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Asignando...
                  </>
                ) : (
                  /**
                   * Reasignar solo si YA hay un radiologo. El estudio nace
                   * con item de lista de trabajo en cuanto se carga, asi que
                   * mirar si el item existe hacia que la primera asignacion
                   * de la vida de un estudio se ofreciera como "Reasignar".
                   */
                  existingWorklistItem?.assignedTo ? 'Reasignar' : 'Asignar'
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
